// Utilitário para tratamento global de erros

export interface ErrorDetails {
  message: string;
  stack?: string;
  timestamp: string;
  userAgent: string;
  url: string;
  userId?: string;
  additionalInfo?: any;
}

export class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;
  private errorQueue: ErrorDetails[] = [];

  private constructor() {
    this.setupGlobalErrorHandlers();
  }

  public static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  private setupGlobalErrorHandlers() {
    // Captura erros JavaScript não tratados
    window.addEventListener('error', (event) => {
      this.handleError(event.error || new Error(event.message), {
        type: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });

    // Captura promises rejeitadas não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        {
          type: 'unhandled_promise_rejection'
        }
      );
    });
  }

  public handleError(error: Error, additionalInfo?: any): void {
    const errorDetails: ErrorDetails = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      additionalInfo
    };

    // Log no console para debugging
    this.logError(errorDetails);

    // Adiciona à fila de erros
    this.errorQueue.push(errorDetails);

    // TODO: Enviar para serviço de monitoramento
    this.sendToMonitoringService(errorDetails);
  }

  private logError(errorDetails: ErrorDetails): void {
    console.group('🚨 Erro Global Capturado');
    console.error('Mensagem:', errorDetails.message);
    console.error('Stack:', errorDetails.stack);
    console.error('Timestamp:', errorDetails.timestamp);
    console.error('URL:', errorDetails.url);
    console.error('Informações adicionais:', errorDetails.additionalInfo);
    console.groupEnd();
  }

  private sendToMonitoringService(errorDetails: ErrorDetails): void {
    // TODO: Implementar integração com serviço de monitoramento
    // Exemplos:
    // - Sentry
    // - LogRocket
    // - Bugsnag
    // - Rollbar
    
    // Por enquanto, apenas armazena localmente para debugging
    try {
      const existingErrors = JSON.parse(localStorage.getItem('wg_tech_errors') || '[]');
      existingErrors.push(errorDetails);
      
      // Manter apenas os últimos 50 erros
      if (existingErrors.length > 50) {
        existingErrors.splice(0, existingErrors.length - 50);
      }
      
      localStorage.setItem('wg_tech_errors', JSON.stringify(existingErrors));
    } catch (e) {
      console.error('Erro ao salvar erro no localStorage:', e);
    }
  }

  public getErrorQueue(): ErrorDetails[] {
    return [...this.errorQueue];
  }

  public clearErrorQueue(): void {
    this.errorQueue = [];
  }

  // Método para tratar erros de API
  public handleApiError(error: any, endpoint?: string): void {
    const apiError = new Error(
      error.response?.data?.message || 
      error.message || 
      'Erro desconhecido na API'
    );

    this.handleError(apiError, {
      type: 'api_error',
      endpoint,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data
    });
  }

  // Método para tratar erros de componentes React (para uso manual)
  public handleComponentError(error: Error, componentName?: string): void {
    this.handleError(error, {
      type: 'component_error',
      componentName
    });
  }
}

// Instância singleton
export const globalErrorHandler = GlobalErrorHandler.getInstance();

// Hook personalizado para usar o error handler em componentes React
export const useErrorHandler = () => {
  const handleError = (error: Error, additionalInfo?: any) => {
    globalErrorHandler.handleError(error, additionalInfo);
  };

  const handleApiError = (error: any, endpoint?: string) => {
    globalErrorHandler.handleApiError(error, endpoint);
  };

  return { handleError, handleApiError };
};